import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "le_cookie_consent"; // values: "accepted" | "declined"

export function CookieBanner({ t }) {
  const [show, setShow] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    try {
      const choice = window.localStorage.getItem(STORAGE_KEY);
      if (choice !== "accepted" && choice !== "declined") {
        // Delay slightly so the banner doesn't fight the hero entrance animation.
        const id = setTimeout(() => setShow(true), 900);
        return () => clearTimeout(id);
      }
    } catch (_e) {
      setShow(true);
    }
  }, []);

  const persist = (choice) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, choice);
    } catch (_e) {
      /* ignore */
    }
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="cookie-banner"
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:px-6 sm:pb-6 pointer-events-none"
          role="dialog"
          aria-labelledby="cookie-banner-title"
          data-testid="cookie-banner"
        >
          <div className="pointer-events-auto max-w-5xl mx-auto bg-[#1C2331] border border-white/10 rounded-sm shadow-[0_-8px_40px_rgba(0,0,0,0.55)] backdrop-blur-md">
            <div className="flex flex-col md:flex-row md:items-center gap-5 p-5 md:p-6">
              <div className="flex items-start gap-4 flex-1 min-w-0">
                <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-[#0B132B] border border-white/10 rounded-sm">
                  <Cookie size={18} className="text-[#00E5FF]" />
                </div>
                <div className="min-w-0">
                  <div
                    id="cookie-banner-title"
                    className="text-white font-semibold"
                    style={{ fontFamily: "'Outfit', sans-serif", fontSize: "16px" }}
                  >
                    {t.cookies.title}
                  </div>
                  <p
                    className="mt-1 text-[#9BA1A6] leading-relaxed"
                    style={{ fontFamily: "'Manrope', sans-serif", fontSize: "13px" }}
                  >
                    {t.cookies.body}{" "}
                    <button
                      type="button"
                      onClick={() => setExpanded((v) => !v)}
                      className="text-[#00E5FF] hover:underline font-semibold"
                      data-testid="cookie-banner-learn-more"
                    >
                      {expanded ? t.cookies.hide_details : t.cookies.learn_more}
                    </button>
                  </p>

                  <AnimatePresence initial={false}>
                    {expanded && (
                      <motion.ul
                        key="cookie-details"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden mt-3 space-y-2 text-[12px] text-[#9BA1A6]"
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                      >
                        {t.cookies.details.map((d, i) => (
                          <li key={i} className="flex gap-2">
                            <span className="text-[#00E5FF]">▸</span>
                            <span>
                              <span className="text-white font-semibold">{d.label}:</span> {d.text}
                            </span>
                          </li>
                        ))}
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 md:shrink-0">
                <button
                  type="button"
                  onClick={() => persist("declined")}
                  data-testid="cookie-banner-decline"
                  className="px-5 py-3 rounded-sm border border-white/15 text-white text-[12px] font-bold uppercase tracking-[0.12em] hover:border-white/35 transition-colors"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  {t.cookies.decline}
                </button>
                <button
                  type="button"
                  onClick={() => persist("accepted")}
                  data-testid="cookie-banner-accept"
                  className="px-5 py-3 rounded-sm bg-[#00E5FF] hover:bg-[#00C2D6] text-[#0B132B] text-[12px] font-bold uppercase tracking-[0.12em] transition-colors"
                  style={{
                    fontFamily: "'Manrope', sans-serif",
                    boxShadow: "0 0 18px rgba(0,229,255,0.25)",
                  }}
                >
                  {t.cookies.accept}
                </button>
                <button
                  type="button"
                  onClick={() => persist("declined")}
                  aria-label="Close"
                  data-testid="cookie-banner-close"
                  className="hidden md:flex w-10 h-10 items-center justify-center rounded-sm border border-white/10 text-[#9BA1A6] hover:text-white hover:border-white/30 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
